import React, { useState } from "react";
import ProductCard from "@/components/ProductCard";
import { Reveal } from "@/components/Reveal";
import { useContent } from "@/context/ContentContext";
import { usePageMeta } from "@/hooks/usePageMeta";
import { Lock, Apple } from "lucide-react";
import { SpotifyGlyph } from "@/components/SocialLinks";

export default function Shop() {
  usePageMeta(
    "Shop XRABI — Luxury Streetwear | CHILLWOAH",
    "XRABI by Okaytwitchy — heavyweight hoodies, oversized tees, wide sweatpants and limited drops. Secure Shopify checkout with Apple Pay, Shop Pay, cards and supported crypto."
  );
  const { products, content } = useContent();
  const all = products.filter((p) => !p.shop_hidden);
  const FILTERS = ["ALL", ...new Set(all.map((p) => p.category))];
  const [filter, setFilter] = useState("ALL");
  const visible = filter === "ALL" ? all : all.filter((p) => p.category === filter);

  return (
    <div data-testid="shop-page">
      <header className="border-b border-[#1A1A22]">
        <svg className="absolute w-0 h-0" aria-hidden="true" focusable="false">
          <filter id="xrabi-rough">
            <feTurbulence type="fractalNoise" baseFrequency="0.032" numOctaves="4" seed="11" result="n" />
            <feDisplacementMap in="SourceGraphic" in2="n" scale="11" />
          </filter>
        </svg>
        <div data-testid="shop-promo-banner" className="w-full overflow-hidden bg-[#0F0F14]">
          <img
            src="/shop/shop-promo.webp"
            alt="CHILLWOAH XRABI campaign — two models wearing the drop. Shop XRABI, the drop, limited release."
            fetchPriority="high"
            className="block w-full h-auto"
            draggable={false}
          />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
          <p className="font-mono text-xs sm:text-sm font-semibold tracking-[0.3em] uppercase text-[#F26522]">The Fashion of CHILLWOAH</p>
          <h1
            className="font-xrabi mt-2 font-normal uppercase text-5xl sm:text-6xl text-white tracking-wide leading-[1]"
            style={{ transform: "skewX(-6deg) rotate(-2deg)", filter: "url(#xrabi-rough)" }}
          >
            XRABI
          </h1>
          <p className="mt-3 max-w-2xl text-zinc-300">
            Heavyweight. Limited. Brooklyn. Real products, real inventory and secure checkout run through Shopify —
            Apple Pay, Shop Pay, cards and supported crypto appear at checkout when enabled.
          </p>
        </div>
      </header>

      <div className="sticky top-16 sm:top-20 z-30 bg-[#08080A]/92 backdrop-blur-md border-b border-[#1A1A22]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-2 overflow-x-auto" role="group" aria-label="Filter products">
          {FILTERS.map((f) => (
            <button
              key={f}
              data-testid={`shop-filter-${f.toLowerCase()}`}
              onClick={() => setFilter(f)}
              aria-pressed={filter === f}
              className={`h-[52px] px-6 shrink-0 font-display font-bold uppercase tracking-widest text-sm border transition-colors ${
                filter === f ? "bg-[#F26522] text-black border-[#F26522]" : "border-[#2A2A36] text-white hover:border-[#F26522]"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {visible.map((p, i) => (
            <Reveal key={p.id} delay={(i % 3) * 0.08} className="h-full">
              <ProductCard product={p} index={i} />
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-16">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 border border-[#1A1A22] bg-[#0F0F14] p-6 sm:p-8">
            <div className="flex items-start gap-4">
              <Lock className="w-6 h-6 text-[#F26522] shrink-0" aria-hidden="true" />
              <p className="text-zinc-300 text-sm">
                <span className="font-display font-bold uppercase text-white block mb-1">Secure checkout</span>
                Shopify processes every order. This site never touches your payment details.
              </p>
            </div>
            <div className="flex items-start gap-4">
              <Apple className="w-6 h-6 text-[#F26522] shrink-0" aria-hidden="true" />
              <p className="text-zinc-300 text-sm">
                <span className="font-display font-bold uppercase text-white block mb-1">Apple Pay &amp; Shop Pay</span>
                Accelerated checkouts appear automatically at Shopify checkout on eligible devices.
              </p>
            </div>
            <div className="flex items-start gap-4">
              <SpotifyGlyph className="w-6 h-6 text-[#F26522]" />
              <p className="text-zinc-300 text-sm">
                <span className="font-display font-bold uppercase text-white block mb-1">Crypto-ready</span>
                Supported cryptocurrency payments can be switched on later at the payment provider — nothing here
                fakes it.
              </p>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
